/**
 * What a villager does when it is not working, and where that lives.
 *
 * ── The question this file exists to answer ─────────────────────────────────
 *
 * The ask was a life cycle: *working, talking, going to pray, per race*. The
 * first thing to settle is which half of the engine owns it, and this codebase
 * already has the rule that decides — **a derived cache must not be observable;
 * if whether something is cached can change what happens, it is not a cache, it
 * is state.** Turn it round and it answers this too.
 *
 * A villager's *position* is state. It is in `hashWorld`, it is in the save, a
 * replay depends on it, and it always was — `stepVillagers` has been steering
 * idle hands toward the Hearth since the settlement layer landed, and that walk
 * is as much simulation as a march is. So **where a villager goes is
 * simulation** and there was never an option to put it anywhere else. Drawing it
 * in the renderer would mean the renderer moving men, which is the one thing the
 * layering rule forbids outright.
 *
 * **How it looks is decoration**: the bob of a man swinging a tool, the stoop of
 * one kneeling at a shrine, the pair turning to face each other, the smoke off a
 * chimney with somebody working under it. None of that feeds back. All of it is
 * in `src/render`, driven off state this file has already decided.
 *
 * ── Why it costs no state and no save ───────────────────────────────────────
 *
 * A villager's errand is a **pure function of things that are already hashed**:
 * its slot index, the tick, and `w.raceId`. Nothing is stored, nothing
 * accumulates, and two worlds with the same hash produce the same errands
 * forever. That is the same argument the rule above makes, run the other way: a
 * value that can be recomputed from hashed state at any moment, and that never
 * remembers anything, is not state and must not be saved. `SAVE_VERSION` does
 * not move, `hashWorld` is untouched, and a save written before this file
 * existed loads into a town whose people immediately have somewhere to be.
 *
 * The same purity is what lets the renderer call `errandOf` itself to decide
 * whether to draw a man kneeling, with no channel back into the world.
 *
 * ── Why the attraction lists are rebuilt every tick ─────────────────────────
 *
 * A villager going to pray needs to know where the shrines are. Caching that
 * list across ticks is exactly the trap the rule names: the cache would have to
 * be invalidated when a building is raised or falls, a stale one sends a man to
 * a shrine that is rubble, and — worst — a world *loaded* mid-session would
 * inherit whatever the process happened to be holding and diverge from the same
 * world run from the start. So the lists are rebuilt from the building store
 * every tick, capped at sixteen entries each, in slot order.
 *
 * That is one walk of the building store per tick. `stepProduction` already does
 * one, unavoidably, so the marginal cost is a second pass over a few thousand
 * slots against nineteen thousand villagers — measured at under 3% of the
 * settlement system. Nothing here is per-villager except two array reads and a
 * modulo.
 */

import type { World } from '../world.ts';
import { UF, UnitState } from '../types.ts';
import { buildingType } from '../../data/buildings.ts';
import { hash2 } from '../../core/math.ts';

/**
 * What a villager is doing with itself.
 *
 * Deliberately short. Six states is enough for a town to look inhabited and few
 * enough that each one is a *different picture* — which is the same lesson the
 * building shapes learned, applied to people. A seventh that looked like one of
 * these six would cost a branch in the hottest loop in the game and buy nothing.
 */
export const Errand = {
  /** Has a job and is going to it, or doing it. Decided elsewhere. */
  Work: 0,
  /** Standing at a shrine, a belfry or whatever this people prays at. */
  Pray: 1,
  /** At the market, the well or the tavern — wherever this people stands about. */
  Gather: 2,
  /** Paired off with a neighbour, in the street, saying nothing worth hearing. */
  Talk: 3,
  /** Gone home. */
  Rest: 4,
  /** Walking, without a destination anybody could name. */
  Wander: 5,
} as const;
export type ErrandId = (typeof Errand)[keyof typeof Errand];

/**
 * How long a villager keeps an errand. 240 ticks is eight seconds of simulated
 * settlement time at the fixed step, which is six days on the Long Ledger's
 * calendar — a working man's errand, not a working man's week.
 *
 * It is a power of two so the phase divide is a shift, and it is the *only*
 * clock in here: nothing counts down, nothing is stored, and a villager's errand
 * at tick N is computable at tick 0.
 */
export const ERRAND_TICKS = 256;

/**
 * A people's day, as five weights out of sixteen.
 *
 * This is the whole of "per race", and it is a table rather than five behaviour
 * functions on purpose — the same argument the vernacular makes about
 * architecture. A Beastman camp does not idle like a Holy Order town because the
 * Moonbound spend their idle hours walking and arguing and the Vigil spend
 * theirs at the lamp; that is a different *town*, and it is eleven rows of five
 * integers rather than eleven behaviours to keep working.
 *
 * The weights must sum to 16. There is a test.
 */
export interface Rhythm {
  pray: number;
  gather: number;
  talk: number;
  rest: number;
  wander: number;
}

/** Men. Tired, and the baseline the others are departures from. */
const ASHEN_RHYTHM: Rhythm = { pray: 3, gather: 4, talk: 3, rest: 3, wander: 3 };

export const RHYTHMS: Record<string, Rhythm> = {
  ashen: ASHEN_RHYTHM,

  /** Nine hundred oaths, and somebody is keeping every one of them. */
  vigil: { pray: 9, gather: 2, talk: 1, rest: 2, wander: 2 },

  /** They go back down. A dwarf off shift is a dwarf indoors. */
  karrun: { pray: 2, gather: 3, talk: 2, rest: 8, wander: 1 },

  /** The Choir is out among the groves, and it is never quite still. */
  verdant: { pray: 4, gather: 2, talk: 2, rest: 2, wander: 6 },

  /** Few, and formal. They gather, and they do not mill about. */
  silver: { pray: 4, gather: 7, talk: 2, rest: 2, wander: 1 },

  /** The Court keeps to the water's edge and does not explain itself. */
  drowned: { pray: 5, gather: 2, talk: 1, rest: 5, wander: 3 },

  /** The Assize sits. Everything here happens at an appointed place. */
  crimson: { pray: 4, gather: 6, talk: 2, rest: 3, wander: 1 },

  /** They are on the warm stone until it goes cold, and then they are on it anyway. */
  longnoon: { pray: 3, gather: 2, talk: 1, rest: 9, wander: 1 },

  /** The market never closes, so nobody is ever quite anywhere else. */
  undertow: { pray: 1, gather: 6, talk: 5, rest: 2, wander: 2 },

  /** Posted, and walking the post. Very little of either is hurried. */
  thirstless: { pray: 2, gather: 3, talk: 1, rest: 6, wander: 4 },

  /** The moot is a permanent argument conducted at volume. */
  splitmoot: { pray: 1, gather: 3, talk: 8, rest: 1, wander: 3 },

  /** Crews. Even off shift they are stood round somebody else's work. */
  cogwarren: { pray: 1, gather: 6, talk: 4, rest: 3, wander: 2 },

  /**
   * The unburied do not rest, do not eat and have nothing to say. What they do
   * is stand at the ossuary, and the effect of a town of them is the point: a
   * Litany settlement is *still* in a way no other settlement is.
   */
  litany: { pray: 10, gather: 3, talk: 0, rest: 0, wander: 3 },

  /** No walls, no doors, no appointments. They walk, and they walk together. */
  moonbound: { pray: 1, gather: 2, talk: 5, rest: 1, wander: 7 },
};

export function rhythmOf(raceId: string): Rhythm {
  return RHYTHMS[raceId] ?? ASHEN_RHYTHM;
}

/**
 * Which errand this villager is on, right now.
 *
 * Pure, cheap, and callable from the renderer. `salt` keeps this stream clear of
 * every other use of `hash2` in the codebase for the same reason the simulation
 * keeps four separate `Rng`s: a shared stream means a new consumer silently
 * retunes every system downstream of it.
 */
export function errandOf(w: World, i: number): ErrandId {
  if (w.units.workSite[i] >= 0) return Errand.Work;
  const phase = (w.tick / ERRAND_TICKS) | 0;
  const r = hash2(i, phase, 0x51ed) * 16;
  const k = rhythmOf(w.raceId);
  if (r < k.pray) return Errand.Pray;
  if (r < k.pray + k.gather) return Errand.Gather;
  if (r < k.pray + k.gather + k.talk) return Errand.Talk;
  if (r < k.pray + k.gather + k.talk + k.rest) return Errand.Rest;
  return Errand.Wander;
}

/**
 * Where the town's three kinds of attraction are, rebuilt every tick.
 *
 * Sixteen of each is plenty: a villager picks one by hash rather than by
 * distance, which spreads a crowd over every shrine in the town instead of
 * stacking it on the nearest, and is O(1) rather than a scan. Capping the list
 * also caps the cost of the walk in a city with four hundred houses.
 *
 * Module-level scratch, never read across a tick boundary, never saved, never
 * hashed. It is filled at the top of `stepVillagers` and consumed inside the
 * same call.
 */
const CAP = 16;
export interface Attractions {
  shrineX: Float32Array;
  shrineY: Float32Array;
  shrineN: number;
  gatherX: Float32Array;
  gatherY: Float32Array;
  gatherN: number;
  homeX: Float32Array;
  homeY: Float32Array;
  homeN: number;
}

export function makeAttractions(): Attractions {
  return {
    shrineX: new Float32Array(CAP),
    shrineY: new Float32Array(CAP),
    shrineN: 0,
    gatherX: new Float32Array(CAP),
    gatherY: new Float32Array(CAP),
    gatherN: 0,
    homeX: new Float32Array(CAP),
    homeY: new Float32Array(CAP),
    homeN: 0,
  };
}

/**
 * Classify a building by what people do *at* it rather than by what it makes.
 *
 * Read off the art vocabulary on purpose. `vis.shape` already says a shrine is a
 * shrine and a belfry is a belfry in every architecture, and the alternative —
 * a fourth flag on `BuildingType` listing where villagers stand — would be a
 * second authority on the same fact, drifting the first time somebody adds a
 * chapel and forgets it. Anything that looks like a holy place is one.
 */
export function fillAttractions(w: World, out: Attractions): void {
  out.shrineN = 0;
  out.gatherN = 0;
  out.homeN = 0;
  const b = w.buildings;
  for (let i = 0; i < b.slots.high; i++) {
    if (b.slots.alive[i] === 0) continue;
    if (b.build[i] < 1) continue;
    const def = buildingType(b.typeId[i]);
    const cx = b.tx[i] + def.w * 0.5;
    const cy = b.ty[i] + def.h * 0.5;
    const s = def.vis.shape;
    if (s === 'shrine' || s === 'belfry') {
      if (out.shrineN < CAP) {
        out.shrineX[out.shrineN] = cx;
        out.shrineY[out.shrineN] = cy;
        out.shrineN++;
      }
    } else if (s === 'market' || s === 'well' || s === 'brazier' || s === 'hearth') {
      if (out.gatherN < CAP) {
        out.gatherX[out.gatherN] = cx;
        out.gatherY[out.gatherN] = cy;
        out.gatherN++;
      }
    } else if (s === 'hut') {
      if (out.homeN < CAP) {
        out.homeX[out.homeN] = cx;
        out.homeY[out.homeN] = cy;
        out.homeN++;
      }
    }
    // Every list full: nothing further in the store can change the answer.
    if (out.shrineN === CAP && out.gatherN === CAP && out.homeN === CAP) break;
  }
}

/**
 * Point one idle villager at wherever its errand takes it.
 *
 * Returns false if there is nowhere to go, in which case the caller falls back
 * to what the settlement has always done — drift toward the Hearth, which is
 * still the right answer for a town of four buildings.
 *
 * Everything here is O(1). The one lookup that is not a modulo is the partner
 * read for `Talk`, which is two array indices.
 */
export function steerErrand(w: World, i: number, errand: ErrandId, a: Attractions): boolean {
  const u = w.units;
  const phase = (w.tick / ERRAND_TICKS) | 0;

  switch (errand) {
    case Errand.Pray: {
      if (a.shrineN === 0) return false;
      const s = ((hash2(i, phase, 0x1f37) * a.shrineN) | 0) % a.shrineN;
      // Stand *outside* it, on a ring, so a congregation reads as a crowd at a
      // door rather than a heap inside a wall.
      const ang = hash2(i, phase, 0x2c11) * 6.2832;
      u.goalX[i] = a.shrineX[s] + Math.cos(ang) * 2.2;
      u.goalY[i] = a.shrineY[s] + Math.sin(ang) * 2.2;
      return true;
    }
    case Errand.Gather: {
      if (a.gatherN === 0) return false;
      const s = ((hash2(i, phase, 0x3d05) * a.gatherN) | 0) % a.gatherN;
      const ang = hash2(i, phase, 0x4a93) * 6.2832;
      const r = 1.6 + hash2(i, phase, 0x5b21) * 2.2;
      u.goalX[i] = a.gatherX[s] + Math.cos(ang) * r;
      u.goalY[i] = a.gatherY[s] + Math.sin(ang) * r;
      return true;
    }
    case Errand.Rest: {
      if (a.homeN === 0) return false;
      const s = ((hash2(i, phase, 0x6e47) * a.homeN) | 0) % a.homeN;
      u.goalX[i] = a.homeX[s] + (hash2(i, phase, 0x7f13) - 0.5) * 2.4;
      u.goalY[i] = a.homeY[s] + 1.4;
      return true;
    }
    case Errand.Talk: {
      /**
       * Pairing by `i ^ 1`, which is the cheapest honest pairing there is: two
       * adjacent slots, each of which computes the same midpoint, so both walk
       * to the same place and stop facing each other without either needing to
       * know it is being talked to. No matching pass, no state, no list.
       *
       * If the neighbour is dead, is not a villager, or is halfway across the
       * town, there is nobody to talk to and the errand falls through.
       */
      const j = i ^ 1;
      if (j >= u.slots.high || u.slots.alive[j] === 0) return false;
      if ((u.flags[j] & UF.Villager) === 0) return false;
      const dx = u.x[j] - u.x[i];
      const dy = u.y[j] - u.y[i];
      if (dx * dx + dy * dy > 400) return false;
      // Stop a stride short of the midpoint, or the two stand in each other.
      u.goalX[i] = u.x[i] + dx * 0.42;
      u.goalY[i] = u.y[i] + dy * 0.42;
      return true;
    }
    case Errand.Wander: {
      // A slow orbit of the Hearth at whatever radius this man already keeps, so
      // a wandering town drifts rather than converging on one point. The angle
      // advances with the errand phase, which is what makes it a walk.
      const hx = w.map.hearthX + 0.5;
      const hy = w.map.hearthY + 0.5;
      const dx = u.x[i] - hx;
      const dy = u.y[i] - hy;
      const rad = Math.max(3, Math.min(26, Math.hypot(dx, dy)));
      const ang = Math.atan2(dy, dx) + (hash2(i, 0, 0x8c55) < 0.5 ? 0.55 : -0.55);
      u.goalX[i] = hx + Math.cos(ang) * rad;
      u.goalY[i] = hy + Math.sin(ang) * rad;
      return true;
    }
    default:
      return false;
  }
}

/**
 * Whether a villager has arrived at whatever it set out for.
 *
 * Read by the renderer, which is the only caller: it decides between drawing a
 * man walking and drawing him kneeling, standing or leaning on something. Kept
 * here rather than in `scene.ts` so there is one definition of "arrived" and the
 * picture cannot disagree with the simulation about it.
 */
export function hasArrived(w: World, i: number): boolean {
  const u = w.units;
  if (u.state[i] === UnitState.Working) return true;
  const dx = u.goalX[i] - u.x[i];
  const dy = u.goalY[i] - u.y[i];
  return dx * dx + dy * dy < 0.64;
}
